<!-- Full-screen focus view for the current timer. Read-only display with start/stop controls. -->
<template>
  <Teleport to="body">
    <div
      ref="dialogRef"
      class="focus-mode"
      role="dialog"
      aria-modal="true"
      aria-label="Focus mode"
      tabindex="-1"
      @keydown="handleKeydown"
    >
      <button ref="closeButtonRef" class="focus-mode__close" @click="$emit('close')" title="Close (Esc)">
        <span class="icon is-medium">
          <FontAwesomeIcon :icon="faXmark" />
        </span>
      </button>

      <div class="focus-mode__stage">
        <div v-if="timer" class="focus-mode__card">
          <h2
            class="focus-mode__title"
            :class="{ 'focus-mode__title--spacious': !projectTagsLabel && !timer.notes?.trim() }"
          >
            {{ timer.title }}
          </h2>

          <p v-if="projectTagsLabel" class="focus-mode__tags">{{ projectTagsLabel }}</p>

          <div v-if="timer.notes" class="focus-mode__notes content" v-html="noteHtml"></div>

          <div class="focus-mode__time">
            <p class="focus-mode__elapsed" v-html="formatBoldTimecode(elapsedSeconds)"></p>
            <p class="focus-mode__remaining">
              <small v-html="formatRemainingTime(remainingSeconds)"></small>
            </p>
          </div>

          <progress
            v-if="hasScheduledDuration"
            class="progress focus-mode__progress"
            :class="progressPercentage >= 100 ? 'is-success' : 'is-primary'"
            :value="progressPercentage"
            max="100"
          ></progress>

          <div class="focus-mode__controls">
            <div class="field has-addons">
              <p class="control">
                <button
                  class="button"
                  :class="isTimerActive ? 'is-link' : 'is-primary'"
                  @click="toggleTimer"
                >
                  <span class="icon">
                    <FontAwesomeIcon :icon="isTimerActive ? faStop : faPlay" />
                  </span>
                  <span>{{ isTimerActive ? 'Stop Timer' : 'Start Timer' }}</span>
                </button>
              </p>
              <p class="control" v-if="!store.isFrontendOnly">
                <TimerControlDropdown
                  :timer-id="timer.id"
                  :action-type="isTimerActive ? 'stop' : 'start'"
                  omit-immediate
                  dropdown-button-only
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPlay, faStop, faXmark } from '@fortawesome/free-solid-svg-icons'
import { formatBoldTimecode, formatRemainingTime } from '../utils/time'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import TimerControlDropdown from './TimerControlDropdown.vue'

const emit = defineEmits(['close'])
const props = defineProps({
  timerId: { type: String, default: null }
})
const store = useBlocksStore()
const dialogRef = ref(null)
const closeButtonRef = ref(null)
let previousActiveElement = null

const timer = computed(() => {
  if (props.timerId) return store.blocks.find(b => b.id === props.timerId) || null
  return store.activeTimer || store.currentlyDueTimer || null
})
const isTimerActive = computed(() => timer.value?.isActive ?? false)

const scheduledDurationSeconds = computed(() => {
  if (!timer.value?.startDate || !timer.value?.endDate) return 0
  const totalSeconds = (new Date(timer.value.endDate) - new Date(timer.value.startDate)) / 1000
  return Number.isFinite(totalSeconds) && totalSeconds > 0 ? totalSeconds : 0
})

const hasScheduledDuration = computed(() => scheduledDurationSeconds.value > 0)

const projectTagsLabel = computed(() => {
  if (!timer.value) return ''
  const parts = []
  if (timer.value.project) parts.push(`@${timer.value.project.name}`)
  if (timer.value.tags?.length) parts.push(...timer.value.tags.map(t => `#${t.name}`))
  return parts.join(' ')
})

const noteHtml = computed(() => {
  if (!timer.value?.notes) return ''
  return DOMPurify.sanitize(marked.parse(timer.value.notes, { breaks: true }))
})

const elapsedSeconds = computed(() => {
  if (!timer.value) return 0
  if (timer.value.isActive) return store.elapsedTimeInSeconds
  return timer.value.duration || 0
})

const remainingSeconds = computed(() => {
  if (!timer.value) return 0
  if (timer.value.isActive) return store.remainingTimeInSeconds
  return (timer.value.duration || 0) - scheduledDurationSeconds.value
})

const progressPercentage = computed(() => {
  if (!timer.value || !hasScheduledDuration.value) return 0
  return Math.min(100, Math.floor((elapsedSeconds.value / scheduledDurationSeconds.value) * 100))
})

const toggleTimer = () => {
  if (!timer.value) return
  if (isTimerActive.value) {
    store.stopTimer(timer.value.id)
  } else {
    store.startTimer(timer.value.id)
  }
}

const getFocusableElements = () => {
  if (!dialogRef.value) return []

  return Array.from(
    dialogRef.value.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null)
}

watch(timer, (val) => {
  if (!val) emit('close')
})

const handleKeydown = (e) => {
  if (e.key === 'Escape') {
    emit('close')
    return
  }

  if (e.key !== 'Tab') return

  const focusableElements = getFocusableElements()
  if (!focusableElements.length) {
    e.preventDefault()
    dialogRef.value?.focus()
    return
  }

  const currentIndex = focusableElements.indexOf(document.activeElement)

  if (e.shiftKey) {
    if (currentIndex <= 0) {
      e.preventDefault()
      focusableElements[focusableElements.length - 1].focus()
    }
    return
  }

  if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
    e.preventDefault()
    focusableElements[0].focus()
  }
}

onMounted(() => {
  previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null
  closeButtonRef.value?.focus()
})

onUnmounted(() => {
  previousActiveElement?.focus?.()
})
</script>

<style scoped>
/* ─── Overlay ─── */
.focus-mode {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Radial gradient: bright centre → soft blue edges */
  background: radial-gradient(
    ellipse 130% 110% at 50% 44%,
    #ffffff 0%,
    #ebf2fe 42%,
    #d3e5ff 100%
  );
}

@media (prefers-color-scheme: dark) {
  .focus-mode {
    background: radial-gradient(
      ellipse 130% 110% at 50% 44%,
      #212d45 0%,
      #172038 48%,
      #0e1830 100%
    );
  }
}

/* ─── Close button ─── */
.focus-mode__close {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--bulma-text-weak, var(--bulma-grey));
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 150ms ease, color 150ms ease;
  flex-shrink: 0;
}

.focus-mode__close:hover {
  background-color: rgba(0, 0, 0, 0.07);
  color: var(--bulma-text);
}

@media (prefers-color-scheme: dark) {
  .focus-mode__close:hover {
    background-color: rgba(255, 255, 255, 0.09);
  }
}

/* ─── Content stage (centred, mobile-width) ─── */
.focus-mode__stage {
  width: 100%;
  max-width: 28rem;
  padding: 3rem 1.75rem 2.5rem;
}

.focus-mode__card {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ─── Title ─── */
.focus-mode__title {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  word-break: break-word;
  color: var(--bulma-text-strong, var(--bulma-text));
  margin-bottom: 1rem;
}

.focus-mode__title--spacious {
  margin-bottom: 4rem;
}

/* ─── Project / Tags ─── */
.focus-mode__tags {
  font-size: 1rem;
  color: var(--bulma-text-weak, var(--bulma-grey));
  margin-bottom: 3rem;
}

/* ─── Note ─── */
.focus-mode__notes {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  /* Shrink dynamically so controls are always visible.
     ~28rem accounts for padding, title, tags, time, progress, controls. */
  max-height: max(6rem, calc(100svh - 30rem));
  overflow-y: auto;
  margin-bottom: 1.5rem;
}

@media (prefers-color-scheme: dark) {
  .focus-mode__notes {
    background: rgba(255, 255, 255, 0.05);
  }
}

/* ─── Time display ─── */
.focus-mode__time {
  text-align: center;
  margin-bottom: 2rem;
}

.focus-mode__elapsed {
  font-size: 5rem;
  line-height: 1;
  font-weight: 300;
  letter-spacing: -0.03em;
  color: var(--bulma-text-strong, var(--bulma-text));
  margin-bottom: 0.5rem;
}

/* Bold parts inside the timecode */
.focus-mode__elapsed :deep(b) {
  font-weight: 700;
}

.focus-mode__remaining {
  font-size: 1rem;
  color: var(--bulma-text-weak, var(--bulma-grey));
}

/* ─── Progress bar ─── */
.focus-mode__progress {
  height: 0.6rem;
  border-radius: 0.5rem;
  margin-bottom: 3rem;
}

/* ─── Controls ─── */
.focus-mode__controls {
  display: flex;
  justify-content: center;
}

/* Force the dropdown menu to open upward */
.focus-mode__controls :deep(.dropdown-menu) {
  top: auto;
  bottom: calc(100% + 4px);
  padding-top: 0;
  padding-bottom: 4px;
}
</style>
